import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  formatAppointmentResponseBody,
  isSuccessfulAppointmentResponse,
  readAppointmentResponseBody,
} from '../appointmentResponse';
import { AppointmentResponseErrorDialog } from '../components/AppointmentResponseErrorDialog';
import { AgendaCreationIcon } from '../components/AgendaCreationIcon';
import { AgendaAppointmentDetails, AgendaAppointmentPreview, type AgendaAppointmentInfoData } from '../components/AgendaAppointmentInfo';
import { AgendaMoveConfirmation, AgendaMovePalette, AgendaMovePreview, AgendaMoveTargetStatus } from '../components/AgendaAppointmentMove';
import { useAgendaAppointmentMove } from '../useAgendaAppointmentMove';
import { readAgendaChainPlacement, useAgendaChainPlacement } from '../useAgendaChainPlacement';
import { AgendaChainPlacementConfirmation } from '../components/AgendaChainPlacementConfirmation';
import { getAgendaMoveTarget } from '../agendaMove';
import { API_BASE_URL, PROC_ID_TO_COLOR } from '../constants';
import { t, type TranslationKey } from '../i18n/translations';
import {
  AGENDA_COLUMNS, AGENDA_FILTERS, AGENDA_START_HOUR as dayStartHour,
  AGENDA_END_HOUR as dayEndHour, AGENDA_INTERVAL_COUNT, AGENDA_INTERVAL_MINUTES,
  AGENDA_TOTAL_MINUTES, getVisibleAgendaColumns,
} from '../agendaConfig';
import { agendaDateKey, arrangeAgendaAppointments, getAgendaWeekdays, type AgendaViewMode } from '../agendaPresentation';
import './AgendaPage.css';

function AgendaNavigationIcon({ value, week = false }: { value: number; week?: boolean }) {
  return (
    <span className="agenda-page__calendar-symbol" aria-hidden="true">
      <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
        {week ? <path d="M10 4h19v21M15 2v4M25 2v4" /> : null}
        <rect x="3" y="7" width="24" height="23" rx="1.5" />
        <path d="M8 4v6M22 4v6M3 13h24" />
      </svg>
      <span>{value}</span>
    </span>
  );
}

// Use /api/appointments/get-procedure-appointment/{id}:
interface ProcedureAppointment {
  id: string;
  when: string;
  duration: number;
  patientIds: string[];
  procedureIds?: string[];
  type: string;
  procedureName: string;
  procedureType: string;
  description?: string;
  // Unused fields:
  // userIds: string[];
  // practitionerIds: string[];
  // procedureIds: string[];
  // meetingId: string
  // [meetingInfo: string]: any;
  // [appointmentInfo: string]: any;
  // procedureDuration:number;
  // procedureRuleMap: Map<String,Object>
}

interface FullPatient {
  // the FullUser fields:
  id: string;
  customerId: String;
  userName: String;
  firstName: String;
  familyName: String;
  email: String;
  mobile: String;
  userDetailId: String;
  extId: String;
  salutation: String;
  title: String;
  zipCode: String;
  town: String;
  phones: String[];
  addressLines: String[];
  languages: String[];
  dateOfBirth: String;
  comment: String;
  // the fields from Patient:
  officeId: String;
  patientType: String;
  //patientInfo: JSON; // not yet
}

// TODO: use 
// /api/user/full/{userId}
// /api/patients/full/{patientId}

const practitionerUserIds = AGENDA_COLUMNS.flatMap((column) => column.practitionerUserId ? [column.practitionerUserId] : []);
const clickTimeIntervalMinutes = 15;
const calendarWeekdayLabels = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const agendaDateStorageKey = 'agendaDate';
const adminBookingAgendaSelectionKey = 'adminBookingAgendaSelection';
const agendaFilterStorageKey = 'agendaPractitionerFilter';

const readAgendaFilter = () => {
  const stored = localStorage.getItem(agendaFilterStorageKey);
  return AGENDA_FILTERS.find((filter) => filter.label === stored || filter.aliases?.includes(stored || ''))?.label || 'Alle';
};

type AppointmentChainDraftForAgenda = {
  version: 1;
  updatedAt: string;
  startDate: string;
  endDate: string;
  editingProcedureId?: string;
  editingIndex?: number;
  chainAppointments: Array<{
    id: string;
    when?: string;
    practitionerId: string;
    [key: string]: unknown;
  }>;
  proposedDates?: string[];
  [key: string]: unknown;
};

const readAppointmentChainDraftForAgenda = () => {
  const storedDraft = localStorage.getItem('currentAppointmentChain');
  if (!storedDraft) return null;

  //console.log(storedDraft);

  try {
    const draft = JSON.parse(storedDraft) as AppointmentChainDraftForAgenda;

    //console.log(draft);

    if (
      draft.version === 1 &&
      Array.isArray(draft.chainAppointments) &&
      typeof draft.startDate === 'string' &&
      typeof draft.endDate === 'string' &&
      typeof draft.editingProcedureId === 'string' &&
      typeof draft.editingIndex === 'number'
    ) {
      return draft;
    } else {
      return null;
    }

  } catch {
    return null;
  }
};

interface AgendaAppointment extends ProcedureAppointment {
  practitionerUserId: string;
}

interface PendingAgendaAction {
  practitionerId?: string;
  selectedStartTime: string;
  selectedDateTime: string;
}

const addDays = (date: Date, days: number) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const adjustClickTime = (totalMinutes: number) => {
  return Math.floor(totalMinutes / clickTimeIntervalMinutes) * clickTimeIntervalMinutes;
};

const addMonths = (date: Date, months: number) => {
  const next = new Date(date);
  const dayOfMonth = next.getDate();
  next.setDate(1);
  next.setMonth(next.getMonth() + months);
  const lastDay = new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate();
  next.setDate(Math.min(dayOfMonth, lastDay));
  return next;
};

const formatDate = (date: Date) => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

const formatOptionalDate = (value?: string) => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const getPatientInitials = (firstName?: string) =>
  (firstName ?? '')
    .trim()
    .split(/[\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase())
    .join(' ');

const formatPatientDisplayName = (patient?: FullPatient) => {
  if (!patient) {
    return '';
  }

  const initials = getPatientInitials(String(patient.firstName ?? ''));
  const familyName = String(patient.familyName ?? '').trim();
  return [initials, familyName].filter(Boolean).join(' ').trim();
};

const formatPatientFullName = (patient?: FullPatient) => {
  if (!patient) {
    return '';
  }

  const firstName = String(patient.firstName ?? '').trim();
  const familyName = String(patient.familyName ?? '').trim();
  return [firstName, familyName].filter(Boolean).join(' ').trim();
};

const translateProcedureName = (procedureName?: string) => {
  if (!procedureName) {
    return 'Behandlung';
  }

  const translationKey = `pro-${procedureName}` as TranslationKey;
  const translatedName = t(translationKey) || procedureName;

  //console.log(procedureName + " -> " + translationKey + " -> " + t(translationKey));

  return translatedName.charAt(0).toUpperCase() + translatedName.slice(1);
};

const parseDateInput = (value: string) => {
  const match = value.trim().match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (!match) {
    return null;
  }

  const [, dayValue, monthValue, yearValue] = match;
  const day = Number(dayValue);
  const month = Number(monthValue);
  const year = Number(yearValue);
  const parsed = new Date(year, month - 1, day);

  if (
    Number.isNaN(parsed.getTime()) ||
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month - 1 ||
    parsed.getDate() !== day
  ) {
    return null;
  }

  return parsed;
};

const parseStoredAgendaDate = (value: string | null) => {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed;
};

const getInitialAgendaDate = () => parseStoredAgendaDate(localStorage.getItem(agendaDateStorageKey)) ?? new Date();

const formatWeekDay = (date: Date) => {
  const day = new Intl.DateTimeFormat('de-CH', { weekday: 'short' }).format(date);
  return (day.charAt(0).toUpperCase() + day.slice(1)).replace(/\.$/, '') + '.';
};

const formatLocalDateTime = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

const startOfDay = (date: Date) => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
};

const endOfDay = (date: Date) => {
  const next = new Date(date);
  next.setHours(23, 59, 59, 0);
  return next;
};

const startOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);

const isSameDay = (left: Date, right: Date) =>
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();

const getCalendarDays = (monthDate: Date) => {
  const monthStart = startOfMonth(monthDate);
  const startWeekday = (monthStart.getDay() + 6) % 7;
  const gridStart = addDays(monthStart, -startWeekday);

  return Array.from({ length: 42 }, (_, index) => addDays(gridStart, index));
};

const formatMonthYear = (date: Date) =>
  new Intl.DateTimeFormat('de-CH', { month: 'long', year: 'numeric' }).format(date);

const getWeekOfYear = (date: Date) => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  next.setDate(next.getDate() + 3 - ((next.getDay() + 6) % 7));
  const firstThursday = new Date(next.getFullYear(), 0, 4);
  firstThursday.setHours(0, 0, 0, 0);
  firstThursday.setDate(firstThursday.getDate() + 3 - ((firstThursday.getDay() + 6) % 7));
  return 1 + Math.round((next.getTime() - firstThursday.getTime()) / (7 * 24 * 60 * 60 * 1000));
};

const getAgendaAppointmentColor = (procedureId?: string) => {
  if (!procedureId) {
    return {
      backgroundColor: 'rgb(229, 231, 235)',
      borderColor: 'rgb(209, 213, 219)',
      textColor: '#374151',
    };
  }

  const color = PROC_ID_TO_COLOR[procedureId];
  if (!color) {
    return {
      backgroundColor: 'rgb(229, 231, 235)',
      borderColor: 'rgb(209, 213, 219)',
      textColor: '#374151',
    };
  }

  return {
    backgroundColor: `rgb(${color.r}, ${color.g}, ${color.b})`,
    borderColor: `rgb(${Math.max(color.r - 35, 0)}, ${Math.max(color.g - 35, 0)}, ${Math.max(color.b - 35, 0)})`,
    textColor: color.r + color.g + color.b > 430 ? '#1f2937' : '#ffffff',
  };
};

export function AgendaPage({ initialViewMode = 'day' }: { initialViewMode?: AgendaViewMode }) {
  const [isSelectingAppointmentChainSlot] = useState(() => Boolean(readAppointmentChainDraftForAgenda()));
  const [chainPlacementDraft] = useState(readAgendaChainPlacement);
  const [selectedDate, setSelectedDate] = useState(getInitialAgendaDate);
  const [selectedFilter, setSelectedFilter] = useState(readAgendaFilter);
  const [viewMode, setViewMode] = useState<AgendaViewMode>(initialViewMode);
  const [unavailableDateHoverTexts, setUnavailableDateHoverTexts] = useState<Record<string, string>>({});
  const [dateInputValue, setDateInputValue] = useState(() => formatDate(getInitialAgendaDate()));
  const [appointments, setAppointments] = useState<AgendaAppointment[]>([]);
  const [patientsById, setPatientsById] = useState<Record<string, FullPatient>>({});
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [appointmentsError, setAppointmentsError] = useState('');
  const [appointmentRefresh, setAppointmentRefresh] = useState(0);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => startOfMonth(getInitialAgendaDate()));
  const [pendingAgendaAction, setPendingAgendaAction] = useState<PendingAgendaAction | null>(null);
  const [selectedAppointment, setSelectedAppointment] = useState<AgendaAppointmentInfoData | null>(null);
  const [appointmentPreview, setAppointmentPreview] = useState<{ key: string; appointment: AgendaAppointmentInfoData; anchor: { x: number; y: number } } | null>(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [deletingAppointment, setDeletingAppointment] = useState(false);
  const [deleteAppointmentError, setDeleteAppointmentError] = useState('');
  const calendarRef = useRef<HTMLDivElement | null>(null);
  const agendaRef = useRef<HTMLDivElement | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const visibleColumns = useMemo(() => getVisibleAgendaColumns(selectedFilter), [selectedFilter]);
  const visibleDates = useMemo(() => viewMode === 'week' ? getAgendaWeekdays(selectedDate) : [selectedDate], [selectedDate, viewMode]);
  const visibleProviderIds = useMemo(() => visibleColumns.flatMap((column) => column.practitionerId ? [column.practitionerId] : []), [visibleColumns]);
  const calendarDays = useMemo(() => getCalendarDays(calendarMonth), [calendarMonth]);

  const move = useAgendaAppointmentMove({
    visibleDates, appointments, loading: loadingAppointments, loadError: appointmentsError,
    onMoved: () => setAppointmentRefresh((version) => version + 1)
  });
  const placement = useAgendaChainPlacement({
    draft: chainPlacementDraft, visibleDates, providers: visibleProviderIds, appointments,
    loading: loadingAppointments, loadError: appointmentsError, colorForProcedure: getAgendaAppointmentColor, onReturn: () => navigate('/appointment-chain')
  });

  const drag = chainPlacementDraft ? placement : move;
  const movingAppointment = move.moving;
  const cancelMove = move.cancel;

  useEffect(() => {
    if (!movingAppointment) return;
    const cancelOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !document.querySelector('[role="dialog"], [role="alertdialog"]')) {
        event.preventDefault(); cancelMove();
      }
    };
    document.addEventListener('keydown', cancelOnEscape);
    return () => document.removeEventListener('keydown', cancelOnEscape);
  }, [movingAppointment, cancelMove]);

  const placingAppointment = placement.moving;
  const cancelPlacement = placement.cancel;
  useEffect(() => {
    if (!placingAppointment) return;
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !document.querySelector('[role="dialog"], [role="alertdialog"]')) {
        event.preventDefault(); cancelPlacement();
      }
    };
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, [placingAppointment, cancelPlacement]);

  useEffect(() => { setAppointmentPreview(null); }, [visibleDates, selectedFilter]);

  useEffect(() => {
    if (!appointmentPreview) return;
    const dismiss = () => setAppointmentPreview(null);
    const dismissOnEscape = (event: KeyboardEvent) => { if (event.key === 'Escape') dismiss(); };
    window.addEventListener('scroll', dismiss, true);
    window.addEventListener('resize', dismiss);
    document.addEventListener('keydown', dismissOnEscape);
    return () => {
      window.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('resize', dismiss);
      document.removeEventListener('keydown', dismissOnEscape);
    };
  }, [appointmentPreview]);

  useEffect(() => {
    if (!isDeleteConfirmationOpen) return;
    const previous = document.activeElement as HTMLElement | null;
    const dialog = agendaRef.current?.querySelector<HTMLElement>(deleteAppointmentError ? '.agenda-page__appointment-error [role="alertdialog"]' : '[role="alertdialog"]');
    dialog?.querySelector<HTMLButtonElement>('button')?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !deletingAppointment) {
        event.preventDefault();
        if (deleteAppointmentError) setDeleteAppointmentError('');
        else setIsDeleteConfirmationOpen(false);
      }
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('keydown', closeOnEscape);
      if (previous?.isConnected) previous.focus();
    };
  }, [isDeleteConfirmationOpen, deletingAppointment, deleteAppointmentError]);

  const goPreviousDay = () => setSelectedDate((current) => addDays(current, viewMode === 'week' ? -7 : -1));
  const goToday = () => setSelectedDate(new Date());
  const goNextDay = () => setSelectedDate((current) => addDays(current, viewMode === 'week' ? 7 : 1));
  const goNextTwoDays = () => setSelectedDate((current) => addDays(current, 2));
  const goNextFourDays = () => setSelectedDate((current) => addDays(current, 4));
  const goForwardMonths = (months: number) => setSelectedDate((current) => addMonths(current, months));
  const openCalendar = () => {
    setCalendarMonth(startOfMonth(selectedDate));
    setIsCalendarOpen(true);
  };
  const closeCalendar = () => setIsCalendarOpen(false);
  const closeAgendaActionPopup = () => setPendingAgendaAction(null);
  const closeAppointmentPopup = () => {
    if (move.preparing) move.cancel();
    setIsDeleteConfirmationOpen(false);
    setSelectedAppointment(null);
  };

  const editAppointment = () => {
    if (!selectedAppointment) {
      return;
    }

    localStorage.setItem('selectedAppointmentId', selectedAppointment.id);
    closeAppointmentPopup();
    navigate('/admin-booking');
  };

  const deleteAppointment = async () => {
    if (!selectedAppointment || deletingAppointment) {
      return;
    }

    const appointmentId = selectedAppointment.id;
    const token = localStorage.getItem('token');
    setDeletingAppointment(true);
    setDeleteAppointmentError('');

    try {
      // TODO: Ergodent hardcoded:
      const response = await fetch(`${API_BASE_URL}/appointments/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          id: appointmentId,
          mode: "ergodent:demo",
        }),
      });

      const responseBody = await readAppointmentResponseBody(response);
      if (!isSuccessfulAppointmentResponse(response, responseBody)) {
        setDeleteAppointmentError(`Error: ${formatAppointmentResponseBody(responseBody)}`);
        return;
      }

      setAppointments((current) => current.filter((appointment) => appointment.id !== appointmentId));
      closeAppointmentPopup();
    } catch (error) {
      console.error('Failed to delete appointment', error);
      setDeleteAppointmentError(`Error: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setDeletingAppointment(false);
    }
  };

  const showPreviousCalendarMonth = () => setCalendarMonth((current) => addMonths(current, -1));
  const showNextCalendarMonth = () => setCalendarMonth((current) => addMonths(current, 1));

  const selectCalendarDate = (date: Date) => {
    setSelectedDate(date);
    setCalendarMonth(startOfMonth(date));
    closeCalendar();
  };

  const openAgendaAction = (targetPath: '/admin-booking' | '/appointment-chain') => {
    if (!pendingAgendaAction) {
      return;
    }

    localStorage.setItem('selectedPatientId', '');
    localStorage.setItem('selectedPractitionerId', pendingAgendaAction.practitionerId || '');
    localStorage.setItem('selectedStartTime', pendingAgendaAction.selectedStartTime); // deprecate
    localStorage.setItem('selectedDateTime', pendingAgendaAction.selectedDateTime);
    if (targetPath === '/admin-booking') {
      localStorage.removeItem('selectedAppointmentId');
    } else {
      localStorage.removeItem('currentAppointmentChain');
    }
    //localStorage.setItem('suppressLiveChat', 'true');
    setPendingAgendaAction(null);
    navigate(targetPath);
  };

  const handleAgendaContextMenu = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();

    if (chainPlacementDraft) {
      setAppointmentPreview(null);
      void placement.drop(dragTarget(event.clientX, event.clientY));
      return;
    }

    const bounds = event.currentTarget.getBoundingClientRect();
    const clickX = event.clientX - bounds.left;
    const clickY = event.clientY - bounds.top;
    const columnWidth = bounds.width / displayColumns.length;
    const column = displayColumns[Math.floor(clickX / columnWidth)];
    const practitionerId = column?.practitionerId;

    if (!column || (!practitionerId && viewMode === 'day') || !visibleColumns.some((item) => item.practitionerId) || bounds.height <= 0) {
      return;
    }

    const minutesSinceStart = Math.max(0, Math.min(clickY, bounds.height)) / bounds.height * AGENDA_TOTAL_MINUTES;
    const totalMinutes = dayStartHour * 60 + minutesSinceStart;
    const adjustedMinutes = Math.min(dayEndHour * 60, adjustClickTime(totalMinutes));
    const selectedHours = Math.floor(adjustedMinutes / 60);
    const selectedMinutes = adjustedMinutes % 60;
    const selectedStartTime = String(selectedHours).padStart(2, '0') + ':' + String(selectedMinutes).padStart(2, '0');
    const selectedDateTime = new Date(column.date);
    selectedDateTime.setHours(selectedHours, selectedMinutes, 0, 0);

    if (localStorage.getItem(adminBookingAgendaSelectionKey)) {
      localStorage.setItem('selectedPractitionerId', practitionerId || '');
      localStorage.setItem('selectedStartTime', selectedStartTime); // deprecate
      localStorage.setItem('selectedDateTime', formatLocalDateTime(selectedDateTime));
      localStorage.removeItem(adminBookingAgendaSelectionKey);
      navigate('/admin-booking');
      return;
    }

    const appointmentChainDraft = readAppointmentChainDraftForAgenda();

    console.log(appointmentChainDraft);

    if (appointmentChainDraft?.editingProcedureId) {
      const procedureExists = appointmentChainDraft.chainAppointments.some(
        (appointment) => appointment.id === appointmentChainDraft.editingProcedureId
      );

      if (procedureExists) {
        const revisedDraft: AppointmentChainDraftForAgenda = {
          ...appointmentChainDraft,
          updatedAt: new Date().toISOString(),
          editingProcedureId: undefined,
          editingIndex: undefined,
          proposedDates: appointmentChainDraft.proposedDates?.map((proposedDate, index) =>
            appointmentChainDraft.chainAppointments[index]?.id === appointmentChainDraft.editingProcedureId
              ? ''
              : proposedDate
          ),
          chainAppointments: appointmentChainDraft.chainAppointments.map((appointment) =>
            appointment.id === appointmentChainDraft.editingProcedureId
              ? {
                ...appointment,
                when: formatLocalDateTime(selectedDateTime),
                practitionerId: practitionerId || appointment.practitionerId,
              }
              : appointment
          ),
        };

        localStorage.setItem('currentAppointmentChain', JSON.stringify(revisedDraft));
        navigate('/appointment-chain');
        return;
      }
    }

    setPendingAgendaAction({
      practitionerId,
      selectedStartTime,
      selectedDateTime: formatLocalDateTime(selectedDateTime),
    });
  };
  const applyDateInput = () => {
    const parsedDate = parseDateInput(dateInputValue);
    if (!parsedDate) {
      setDateInputValue(formatDate(selectedDate));
      return;
    }

    setSelectedDate(parsedDate);
    setCalendarMonth(startOfMonth(parsedDate));
    setDateInputValue(formatDate(parsedDate));
  };

  useEffect(() => {
    let ignore = false;

    const fetchAppointments = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setAppointments([]);
        setAppointmentsError('Not authenticated');
        return;
      }

      setLoadingAppointments(true);
      setAppointmentsError('');

      try {
        const from = formatLocalDateTime(startOfDay(visibleDates[0]));
        const to = formatLocalDateTime(endOfDay(visibleDates[visibleDates.length - 1]));

        const appointmentGroups = await Promise.all(
          practitionerUserIds.map(async (userId) => {
            const searchResponse = await fetch(`${API_BASE_URL}/appointments/search`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({ userId, from, to }),
            });

            if (!searchResponse.ok) {
              throw new Error(`Search failed for ${userId}: ${searchResponse.status}`);
            }

            const appointmentIds = (await searchResponse.json()) as string[];

            //console.log("appointmentIds=" + appointmentIds + " " + JSON.stringify({ userId, from, to }));

            const details = await Promise.all(
              appointmentIds.map(async (id) => {
                const detailResponse = await fetch(`${API_BASE_URL}/appointments/get-procedure-appointment/${id}`, {
                  headers: {
                    'Authorization': `Bearer ${token}`,
                  },
                });

                if (!detailResponse.ok) {
                  throw new Error(`Appointment load failed for ${id}: ${detailResponse.status}`);
                }

                const appointment = (await detailResponse.json()) as ProcedureAppointment;
                return { ...appointment, practitionerUserId: userId } satisfies AgendaAppointment;
              })
            );

            return details;
          })
        );

        if (!ignore) {
          setAppointments(appointmentGroups.flat());
        }
      } catch (error) {
        console.error('Failed to fetch agenda appointments', error);
        if (!ignore) {
          setAppointments([]);
          setAppointmentsError(error instanceof Error ? error.message : 'Appointments could not be loaded');
        }
      } finally {
        if (!ignore) {
          setLoadingAppointments(false);
        }
      }
    };

    fetchAppointments();

    return () => {
      ignore = true;
    };
  }, [visibleDates, appointmentRefresh]);

  useEffect(() => {
    let ignore = false;

    const fetchPatients = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        return;
      }

      const patientIds = Array.from(
        new Set(
          appointments
            .flatMap((appointment) => appointment.patientIds)
            .map((patientId) => patientId.trim())
            .filter(Boolean)
        )
      );

      const missingPatientIds = patientIds.filter((patientId) => !patientsById[patientId]);
      if (missingPatientIds.length === 0) {
        return;
      }

      try {
        const loadedPatients = await Promise.all(
          missingPatientIds.map(async (patientId) => {
            const response = await fetch(`${API_BASE_URL}/patients/full/${patientId}`, {
              headers: {
                'Authorization': `Bearer ${token}`,
              },
            });

            if (!response.ok) {
              throw new Error(`Patient load failed for ${patientId}: ${response.status}`);
            }

            const patient = (await response.json()) as FullPatient;
            return [patientId, patient] as const;
          })
        );

        if (!ignore) {
          setPatientsById((current) => ({
            ...current,
            ...Object.fromEntries(loadedPatients),
          }));
        }
      } catch (error) {
        console.error('Failed to fetch agenda patients', error);
      }
    };

    fetchPatients();

    return () => {
      ignore = true;
    };
  }, [appointments, patientsById]);

  useEffect(() => {
    setDateInputValue(formatDate(selectedDate));
  }, [selectedDate]);

  useEffect(() => {
    localStorage.setItem(agendaDateStorageKey, selectedDate.toISOString());
  }, [selectedDate]);

  useEffect(() => {
    localStorage.setItem(agendaFilterStorageKey, selectedFilter);
  }, [selectedFilter]);

  useEffect(() => {
    const draft = readAppointmentChainDraftForAgenda();
    if (!draft || !isSelectingAppointmentChainSlot || chainPlacementDraft) return;
    let ignore = false;
    const token = localStorage.getItem('token');

    void fetch(`${API_BASE_URL}/appointments/draft-date-and-time-infos`, {
      //void fetch(`${API_BASE_URL}/appointments/draft-date-infos`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({
        editingIndex: draft.editingIndex,
        startDate: draft.startDate,
        endDate: draft.endDate,
        chainAppointments: draft.chainAppointments,
        officeId: localStorage.getItem('selectedOfficeId'),
      }),
    }).then(async (response) => {
      if (!response.ok) throw new Error(`Draft date infos request failed: ${response.status}`);
      const infos: unknown = await response.json();
      const start = startOfDay(new Date(draft.startDate));
      if (!Array.isArray(infos) || Number.isNaN(start.getTime()) || ignore) return;
      const texts: Record<string, string> = {};
      infos.forEach((info, index) => {
        if (typeof info !== 'string' || info === 'Y') return;
        const [from, to] = info.split('|').map((part) => part.trim());
        texts[agendaDateKey(addDays(start, index))] = info === 'N' ? t('no-appointments-available')
          : from && to ? t('chain-date-range-short-hover').replace('{from}', from).replace('{to}', to) : info;
      });
      setUnavailableDateHoverTexts(texts);
    }).catch((error) => console.error('Failed to load draft date infos', error));
    return () => { ignore = true; };
  }, [isSelectingAppointmentChainSlot, chainPlacementDraft]);

  useLayoutEffect(() => {
    const agenda = agendaRef.current;
    if (!agenda) return;

    const updateHeight = () => {
      const top = agenda.getBoundingClientRect().top + window.scrollY;
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
      const height = `${Math.max(0, viewportHeight - top - 8)}px`;
      if (agenda.style.getPropertyValue('--agenda-height') !== height) {
        agenda.style.setProperty('--agenda-height', height);
      }
      const gridHeight = gridRef.current?.getBoundingClientRect().height ?? 600;
      const quarterHeight = gridHeight / (AGENDA_TOTAL_MINUTES / 15);
      agenda.style.setProperty('--agenda-time-label-limit', `${Math.max(1, quarterHeight - 1).toFixed(2)}px`);
    };

    updateHeight();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateHeight);
    const sidebar = agenda.closest('.portal-layout--shared')?.querySelector('.portal-sidebar');
    if (sidebar) observer?.observe(sidebar);
    if (gridRef.current) observer?.observe(gridRef.current);
    window.addEventListener('resize', updateHeight);
    window.visualViewport?.addEventListener('resize', updateHeight);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updateHeight);
      window.visualViewport?.removeEventListener('resize', updateHeight);
    };
  }, []);

  useEffect(() => {
    if (!isCalendarOpen) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        closeCalendar();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [isCalendarOpen]);

  const positionedAppointments = useMemo(() => visibleDates.flatMap((date) => {
    const dayStart = new Date(date);
    dayStart.setHours(dayStartHour, 0, 0, 0);
    const dayEnd = new Date(date);
    dayEnd.setHours(dayEndHour, 0, 0, 0);
    return appointments.map((appointment) => {
      const columnIndex = practitionerUserIds.indexOf(appointment.practitionerUserId);
      const appointmentStart = new Date(appointment.when);
      const appointmentEnd = new Date(appointmentStart.getTime() + appointment.duration * 60000);
      if (columnIndex < 0 || Number.isNaN(appointmentStart.getTime()) || !Number.isFinite(appointment.duration) || appointment.duration <= 0) return null;
      const visibleStart = Math.max(appointmentStart.getTime(), dayStart.getTime());
      const visibleEnd = Math.min(appointmentEnd.getTime(), dayEnd.getTime());
      if (visibleEnd <= visibleStart) return null;
      const patientId = appointment.patientIds[0] || '';
      const patient = patientsById[patientId];
      return {
        ...appointment, columnIndex, dayKey: agendaDateKey(date),
        practitionerLabel: AGENDA_COLUMNS.find((column) => column.practitionerUserId === appointment.practitionerUserId)?.label || '',
        procedureId: Array.isArray(appointment.procedureIds) ? appointment.procedureIds[0] : undefined,
        patientId, patientDisplayName: formatPatientDisplayName(patient) || patientId,
        patientFullName: formatPatientFullName(patient) || patientId,
        patientDateOfBirth: formatOptionalDate(String(patient?.dateOfBirth ?? '')),
        top: (visibleStart - dayStart.getTime()) / (60000 * AGENDA_TOTAL_MINUTES) * 100,
        height: (visibleEnd - visibleStart) / (60000 * AGENDA_TOTAL_MINUTES) * 100,
      };
    }).filter((appointment): appointment is NonNullable<typeof appointment> => appointment !== null);
  }), [appointments, patientsById, visibleDates]);

  const displayColumns = useMemo(() => viewMode === 'week'
    ? visibleDates.map((date) => ({
      key: agendaDateKey(date), date, label: formatWeekDay(date),
      practitionerId: visibleColumns.length === 1 ? visibleColumns[0].practitionerId : undefined,
      appointments: arrangeAgendaAppointments(positionedAppointments.filter((appointment) =>
        appointment.dayKey === agendaDateKey(date) && visibleColumns.some((column) => column.practitionerUserId === appointment.practitionerUserId))),
    }))
    : visibleColumns.map((column) => ({
      key: column.label, date: selectedDate, label: column.label, practitionerId: column.practitionerId,
      appointments: arrangeAgendaAppointments(positionedAppointments.filter((appointment) => appointment.practitionerUserId === column.practitionerUserId)),
    })), [viewMode, visibleDates, visibleColumns, positionedAppointments, selectedDate]);

  const appointmentInfo = (appointment: (typeof positionedAppointments)[number]): AgendaAppointmentInfoData => ({
    id: appointment.id,
    title: translateProcedureName(appointment.procedureName),
    patientName: appointment.patientFullName,
    dateOfBirth: appointment.patientDateOfBirth,
    description: appointment.description?.trim() || '',
    when: appointment.when,
    duration: appointment.duration,
    practitionerLabel: appointment.practitionerLabel,
    color: getAgendaAppointmentColor(appointment.procedureId),
  });

  const openAppointmentDetails = (appointment: (typeof positionedAppointments)[number]) => {
    setAppointmentPreview(null);
    move.setError('');
    setSelectedAppointment(appointmentInfo(appointment));
  };

  // Patient details may finish loading while the preview or dialog is already open.
  const currentAppointmentInfo = (info: AgendaAppointmentInfoData) => {
    const current = positionedAppointments.find((appointment) => appointment.id === info.id && appointment.practitionerLabel === info.practitionerLabel);
    return current ? appointmentInfo(current) : info;
  };

  const dragTarget = (x: number, y: number) => {
    if (!gridRef.current) return null;
    const target = getAgendaMoveTarget(x, y, gridRef.current.getBoundingClientRect(), displayColumns,
      viewMode === 'week' ? visibleColumns.flatMap((column) => column.practitionerId ? [column.practitionerId] : []) : []);
    return viewMode === 'day' && !target?.practitionerId ? null : target;
  };
  const columnPlacementStatus = (column: { date: Date; practitionerId?: string }) => placement.dateStatus(column.date,
    column.practitionerId ? [column.practitionerId] : visibleProviderIds);

  return (
    <div ref={agendaRef} data-view={viewMode} className="agenda-page overflow-hidden rounded-3xl border border-[#d9d9d6] bg-[#efefe9] text-slate-900">
      <header className="agenda-page__title bg-[#3f4d62] text-white">
        <span>Müller&amp;Weidmann</span>
      </header>

      <section className={`agenda-page__navigation border-b border-[#d0d0ca] bg-[#efefe9]${drag.moving ? ' agenda-page__navigation--moving' : ''}`}>
        <div className="agenda-page__navigation-main">
          <div className="agenda-page__date-navigation flex flex-wrap items-center text-[#1f2630]">
            <div className="agenda-page__day-navigation flex items-center gap-1">
              <button type="button" onClick={goPreviousDay} className="flex h-9 min-w-9 items-center justify-center rounded-md px-2 text-xl font-medium" aria-label={viewMode === 'week' ? 'Eine Woche zurück' : 'Einen Tag zurück'}>
                {'<'}
              </button>
              <button
                type="button"
                onClick={goToday}
                className="flex h-9 w-9 items-center justify-center rounded-full text-[20px] leading-none"
                aria-label="Heute"
              >
                <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
                  <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.2" />
                  <circle cx="12" cy="12" r="2" fill="currentColor" />
                </svg>
              </button>
              <button type="button" onClick={goNextDay} className="flex h-9 min-w-9 items-center justify-center rounded-md px-2 text-xl font-medium" aria-label={viewMode === 'week' ? 'Eine Woche vor' : 'Einen Tag vor'}>
                {'>'}
              </button>
              <button type="button" onClick={goNextTwoDays} className="flex h-9 min-w-9 items-center justify-center rounded-md px-2 text-xl font-medium" aria-label="Zwei Tage vor">
                {'>>'}
              </button>
              <button type="button" onClick={goNextFourDays} className="flex h-9 min-w-9 items-center justify-center rounded-md px-2 text-xl font-medium" aria-label="Vier Tage vor">
                {'>>>'}
              </button>
            </div>

            <div className="agenda-page__month-navigation flex items-center gap-2">
              {[3, 6, 9, 12].map((value) => (
                <div key={value} className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => goForwardMonths(value)}
                    className="agenda-page__month-button"
                    aria-label={`${value} Monate vor`}
                  >
                    <AgendaNavigationIcon value={value} />
                  </button>
                  <span className="text-xl font-medium">{'>'}</span>
                </div>
              ))}
            </div>

            <div ref={calendarRef} className="agenda-page__date-picker relative">
              <div className="agenda-page__date-input flex items-center overflow-hidden rounded-lg border border-[#b7b7b2] bg-[#f5f5f3] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.35)]">
                <input
                  type="text"
                  value={dateInputValue}
                  onChange={(event) => setDateInputValue(event.target.value)}
                  onBlur={applyDateInput}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      applyDateInput();
                      (event.target as HTMLInputElement).blur();
                    }
                  }}
                  className="h-full w-[128px] bg-transparent px-4 outline-none"
                  aria-label="Datum bearbeiten"
                />
                <button
                  type="button"
                  onClick={() => (isCalendarOpen ? closeCalendar() : openCalendar())}
                  className="flex h-full items-center border-l border-[#c9c9c3] px-3 text-[#4b5563]"
                  aria-label="Datum auswählen"
                >
                  <svg viewBox="0 0 20 20" className="h-5 w-5 fill-none stroke-current" strokeWidth="1.8">
                    <rect x="3" y="4.5" width="14" height="12.5" rx="2" />
                    <path d="M6.5 3v3M13.5 3v3M3 8.5h14" />
                  </svg>
                </button>
              </div>

              {isCalendarOpen && (
                <div className="absolute right-0 top-12 z-20 w-[280px] rounded-2xl border border-[#d4d4cd] bg-[#fbfbf8] p-4 shadow-[0_18px_35px_rgba(31,38,48,0.18)]">
                  <div className="mb-3 flex items-center justify-between">
                    <button
                      type="button"
                      onClick={showPreviousCalendarMonth}
                      className="flex h-8 w-8 items-center justify-center rounded-full bg-[#f0efe9] text-lg font-medium text-[#343b46]"
                      aria-label="Vorheriger Monat"
                    >
                      {'<'}
                    </button>
                    <p className="text-sm font-semibold capitalize text-[#343b46]">{formatMonthYear(calendarMonth)}</p>
                    <button
                      type="button"
                      onClick={showNextCalendarMonth}
                      className="flex h-8 w-8 items-center justify-center rounded-full bg-[#f0efe9] text-lg font-medium text-[#343b46]"
                      aria-label="Nächster Monat"
                    >
                      {'>'}
                    </button>
                  </div>

                  <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold uppercase tracking-[0.06em] text-[#7a838f]">
                    {calendarWeekdayLabels.map((label) => (
                      <div key={label} className="py-1">
                        {label}
                      </div>
                    ))}
                  </div>

                  <div className="mt-1 grid grid-cols-7 gap-1">
                    {calendarDays.map((date) => {
                      const isCurrentMonth = date.getMonth() === calendarMonth.getMonth();
                      const isSelected = isSameDay(date, selectedDate);
                      const isToday = isSameDay(date, new Date());

                      return (
                        <button
                          key={date.toISOString()}
                          type="button"
                          onClick={() => selectCalendarDate(date)}
                          className={`flex h-9 items-center justify-center rounded-lg text-sm transition ${isSelected
                            ? 'bg-[#3f69a1] font-semibold text-white'
                            : isCurrentMonth
                              ? 'text-[#2c3440] hover:bg-[#ecebe4]'
                              : 'text-[#a1a7b0] hover:bg-[#f1f0ea]'
                            } ${isToday && !isSelected ? 'border border-[#b7c7de]' : ''}`}
                        >
                          {date.getDate()}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="agenda-page__view-switch" role="group" aria-label="Agendaansicht">
              <button type="button" aria-label="Wochenansicht (5)" title="Montag bis Freitag" aria-pressed={viewMode === 'week'} onClick={() => setViewMode('week')}><AgendaNavigationIcon value={5} week /></button>
              <button type="button" aria-label="Tagesansicht (1)" title="Tagesansicht" aria-pressed={viewMode === 'day'} onClick={() => setViewMode('day')}><AgendaNavigationIcon value={1} /></button>
            </div>
          </div>

          <div className="agenda-page__filters flex flex-wrap items-center font-medium text-[#222]" role="group" aria-label="Behandlerfilter">
            {AGENDA_FILTERS.map((item) => (
              <button key={item.label} type="button" aria-pressed={selectedFilter === item.label} onClick={() => setSelectedFilter(item.label)}>
                {item.label}
              </button>
            ))}
          </div>
          {drag.moving ? <div className="agenda-page__move-status-slot"><AgendaMoveTargetStatus move={drag} /></div> : null}
        </div>
        {drag.moving ? <>
          <div className="agenda-page__move-toolbar">
            <AgendaMovePalette move={drag} placement={Boolean(chainPlacementDraft)} onDragMove={(x, y) => { setAppointmentPreview(null); drag.setHoverTarget(dragTarget(x, y)); }} onPlace={(x, y) => { void drag.drop(dragTarget(x, y)); }} />
          </div>
        </> : null}
      </section>

      <section className="agenda-page__day-header">
        <button
          type="button"
          className="agenda-page__week cursor-pointer border-r border-[#7d90ab] bg-[#3f69a1] text-white"
          onClick={() => setViewMode('week')}
          title="Klicken für Wochenansicht"
          aria-label={`Kalenderwoche ${getWeekOfYear(selectedDate)}`}
        >
          {getWeekOfYear(selectedDate)}
        </button>
        <div className="agenda-page__day bg-[#3f69a1] text-white">
          <span className="agenda-page__day-number">{String(selectedDate.getDate()).padStart(2, '0')}</span>
          <span className="agenda-page__day-meta"><span>{formatWeekDay(selectedDate)}</span><span>{formatDate(selectedDate).slice(3)}</span></span>
        </div>
        <div className="border-r border-[#d0d0cc] bg-[#f0f0eb]" />
        <div
          className={`agenda-page__column-headers grid border-y border-[#b8bdc9] bg-[#e4e6ea] font-semibold text-[#21252c]${viewMode === 'week' ? ' agenda-page__column-headers--week' : ''}`}
          style={{ gridTemplateColumns: `repeat(${displayColumns.length}, minmax(0, 1fr))` }}
          aria-label={viewMode === 'week' ? 'Arbeitstage Montag bis Freitag' : 'Sichtbare Behandler'}
        >
          {displayColumns.map((column) => (
            <div key={column.key} className={`border-l border-[#c7ccd6] text-center first:border-l-0${(chainPlacementDraft ? columnPlacementStatus(column).disabled : viewMode === 'week' && unavailableDateHoverTexts[column.key]) ? ' agenda-page__unavailable' : ''}`}
              data-column={column.key} title={chainPlacementDraft ? columnPlacementStatus(column).message : viewMode === 'week' ? unavailableDateHoverTexts[column.key] : undefined}>
              {viewMode === 'week' ? (
                <button type="button" className="flex h-full w-full flex-col items-center justify-center"
                  aria-label={`Tagesansicht für ${formatDate(column.date)}`}
                  title="Klicken für Tagesansicht"
                  onClick={() => { setSelectedDate(new Date(column.date)); setViewMode('day'); }}>
                  <span>{column.label}</span><span>{formatDate(column.date).slice(0, 5)}</span>
                </button>
              ) : column.label}
            </div>
          ))}
        </div>
      </section>

      <section className="agenda-page__day-grid" aria-label={`${viewMode === 'week' ? 'Wochenagenda' : 'Tagesagenda'} 06:00 bis 19:00`}>
        <div className="agenda-page__times border-r border-[#d0d0cc] bg-[#efefe9]">
          {Array.from({ length: AGENDA_TOTAL_MINUTES / 15 + 1 }, (_, index) => {
            const minute = index * 15;
            const isHour = minute % 60 === 0;
            return <span key={index} data-minute={minute} data-quarter={index % 4}
              className={`agenda-page__time-label${isHour ? ' agenda-page__hour' : ''}`} style={{ top: `${minute / AGENDA_TOTAL_MINUTES * 100}%` }}>
              {isHour ? `${String(dayStartHour + minute / 60).padStart(2, '0')}:00` : minute % 60}
            </span>;
          })}
        </div>
        <div ref={gridRef} className="agenda-page__grid" onContextMenu={handleAgendaContextMenu}>
          <div className="agenda-page__lines" aria-hidden="true">
            {Array.from({ length: AGENDA_INTERVAL_COUNT }, (_, index) => (
              <div key={index}
                className={`agenda-page__grid-row${index * AGENDA_INTERVAL_MINUTES % 60 === 0 ? ' agenda-page__grid-row--hour' : ''}`}
                style={{ top: `${index / AGENDA_INTERVAL_COUNT * 100}%`, height: `${100 / AGENDA_INTERVAL_COUNT}%` }} data-minute={index * AGENDA_INTERVAL_MINUTES} />
            ))}
          </div>
          <div className="agenda-page__columns" style={{ gridTemplateColumns: `repeat(${displayColumns.length}, minmax(0, 1fr))` }}>
            {displayColumns.map((column) => (
              <div key={column.key} className={`agenda-page__column${viewMode === 'week' && unavailableDateHoverTexts[column.key] ? ' agenda-page__unavailable-column' : ''}`}
                data-column={column.key} data-date={agendaDateKey(column.date)} title={viewMode === 'week' ? unavailableDateHoverTexts[column.key] : undefined}>
                {chainPlacementDraft && columnPlacementStatus(column).disabled ? <div className="agenda-chain-placement__disabled" aria-label={columnPlacementStatus(column).message} /> : null}
                {column.appointments.map((appointment) => {
                  const color = getAgendaAppointmentColor(appointment.procedureId);
                  return (
                    <div key={`${appointment.dayKey}-${appointment.practitionerUserId}-${appointment.id}`}
                      role="button" tabIndex={0}
                      aria-label={`Termindetails anzeigen: ${translateProcedureName(appointment.procedureName)}, ${appointment.patientFullName}, ${new Date(appointment.when).toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' })}, ${appointment.practitionerLabel}`}
                      title=""
                      aria-describedby={appointmentPreview?.key === `${appointment.dayKey}-${appointment.practitionerUserId}-${appointment.id}` ? 'agenda-appointment-preview' : undefined}
                      onMouseEnter={(event) => {
                        if (!selectedAppointment && !pendingAgendaAction && !drag.dragging) setAppointmentPreview({ key: `${appointment.dayKey}-${appointment.practitionerUserId}-${appointment.id}`, appointment: appointmentInfo(appointment), anchor: { x: event.clientX, y: event.clientY } });
                      }}
                      onMouseLeave={() => setAppointmentPreview(null)}
                      onBlur={() => setAppointmentPreview(null)}
                      onClick={() => openAppointmentDetails(appointment)}
                      onKeyDown={(event) => { if (event.key === 'Escape') setAppointmentPreview(null); if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); openAppointmentDetails(appointment); } }}
                      onContextMenu={(event) => { event.preventDefault(); event.stopPropagation(); setAppointmentPreview(null); }}
                      className={`agenda-page__appointment cursor-pointer overflow-hidden rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-[#8f7748]${move.moving?.info.id === appointment.id ? ' agenda-page__appointment--moving' : ''}`}
                      data-appointment-id={appointment.id} data-practitioner={appointment.practitionerLabel}
                      style={{
                        top: `${appointment.top}%`, height: `${appointment.height}%`,
                        left: `calc(${appointment.lane / appointment.laneCount * 100}% + 2px)`, width: `calc(${100 / appointment.laneCount}% - 4px)`,
                        backgroundColor: color.backgroundColor, '--agenda-appointment-border': color.borderColor, color: color.textColor
                      } as React.CSSProperties}>
                      <p className="truncate font-semibold leading-tight">{viewMode === 'week' ? `${appointment.practitionerLabel} · ` : ''}{translateProcedureName(appointment.procedureName)}</p>
                      <p className="truncate leading-tight">{appointment.patientDisplayName}</p>
                      <p className="truncate leading-tight">{appointment.patientDateOfBirth}</p>
                      {appointment.description?.trim() ? <p className="truncate leading-tight">{appointment.description.trim()}</p> : null}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
          <AgendaMovePreview move={drag} />
          {(loadingAppointments || appointmentsError) && (
            <div className="pointer-events-none absolute inset-x-0 top-2 z-10 px-3 text-sm text-[#6a7280]">
              {loadingAppointments ? 'Appointments werden geladen…' : appointmentsError}
            </div>
          )}
        </div>
      </section>

      {appointmentPreview && !selectedAppointment && !pendingAgendaAction ? <AgendaAppointmentPreview appointment={currentAppointmentInfo(appointmentPreview.appointment)} anchor={appointmentPreview.anchor} /> : null}

      {selectedAppointment ? (
        <AgendaAppointmentDetails appointment={currentAppointmentInfo(selectedAppointment)} onClose={closeAppointmentPopup}
          onEdit={editAppointment} onDelete={() => setIsDeleteConfirmationOpen(true)}
          onMove={chainPlacementDraft ? undefined : () => { void move.begin(currentAppointmentInfo(selectedAppointment)).then((started) => { if (started) { setAppointmentPreview(null); closeAppointmentPopup(); } }); }}
          preparingMove={move.preparing} moveError={move.error}
          suspended={isDeleteConfirmationOpen || Boolean(deleteAppointmentError)} />
      ) : null}

      {move.pendingTarget ? <AgendaMoveConfirmation move={move} /> : null}
      {placement.pendingTarget ? <AgendaChainPlacementConfirmation placement={placement} /> : null}

      {selectedAppointment && isDeleteConfirmationOpen ? (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/40 px-4"
          onClick={() => {
            if (!deletingAppointment) {
              setIsDeleteConfirmationOpen(false);
            }
          }}
        >
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-delete-appointment-title"
            aria-describedby="confirm-delete-appointment-description"
            className="w-full max-w-md rounded-2xl bg-white p-6 shadow-[0_24px_60px_rgba(19,19,19,0.22)]"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="confirm-delete-appointment-title" className="text-lg font-semibold text-slate-900">
              Termin löschen?
            </h2>
            <p id="confirm-delete-appointment-description" className="mt-3 text-sm leading-6 text-slate-600">
              Möchten Sie diesen Termin wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
            </p>

            <div className="mt-7 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsDeleteConfirmationOpen(false)}
                disabled={deletingAppointment}
                className="rounded-lg border border-[#ded8cc] px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-[#f8f6f1] disabled:cursor-not-allowed disabled:opacity-60"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={() => void deleteAppointment()}
                disabled={deletingAppointment}
                className="rounded-lg bg-[#cbb58a] px-4 py-2 text-sm font-medium text-white transition hover:bg-[#bca477] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {deletingAppointment ? 'Termin wird gelöscht…' : 'Termin löschen'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="agenda-page__appointment-error">
        <AppointmentResponseErrorDialog message={deleteAppointmentError} onClose={() => setDeleteAppointmentError('')} />
      </div>

      {pendingAgendaAction ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/35 px-4"
          onClick={closeAgendaActionPopup}
        >
          <div
            role="dialog" aria-modal="true" aria-labelledby="agenda-create-title" className="agenda-page__creation-dialog w-full max-w-lg rounded-[28px] border border-[#efe7da] bg-white p-7 shadow-[0_24px_60px_rgba(19,19,19,0.18)]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 id="agenda-create-title" className="text-2xl font-semibold tracking-[-0.02em] text-slate-900">Termin erstellen</h2>
                <p className="mt-2 text-sm text-slate-500">
                  {formatDate(new Date(pendingAgendaAction.selectedDateTime))} um {pendingAgendaAction.selectedStartTime}
                </p>
              </div>
              <button
                type="button"
                onClick={closeAgendaActionPopup}
                aria-label="Termin erstellen schliessen"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#efe7da] text-slate-400 transition hover:border-[#cbb58a] hover:text-slate-700"
              >
                <span className="text-xl leading-none">×</span>
              </button>
            </div>

            <div className="agenda-page__creation-options">
              <button type="button" onClick={() => openAgendaAction('/admin-booking')} className="agenda-page__creation-option">
                <AgendaCreationIcon /><span>Einzeltermin</span>
              </button>
              <button type="button" onClick={() => openAgendaAction('/appointment-chain')} className="agenda-page__creation-option agenda-page__creation-option--series">
                <AgendaCreationIcon series /><span>Terminserie</span>
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
